import React, { useState, useEffect } from 'react';

import Button from '../shared/Button';
import { X, LayoutTemplate } from 'lucide-react';

// Options available for each slot
const OPTIONS = [
    { label: 'None', value: 'none' },
    { label: 'Page Number', value: 'pageNumber' },
    { label: 'Running Head / Title', value: 'title' },
];

const PositionSelector = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1 w-full">
        <label className="text-xs font-semibold text-gray-500 uppercase">{label}</label>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full p-2 border border-gray-200 rounded-md text-sm text-dark-blue focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple transition-colors"
        >
            {OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    </div>
);

const SectionConfig = ({ title, config, onChange }) => (
    <div className="flex flex-col p-4 bg-gray-50 rounded-lg border border-gray-100">
        <h4 className="font-semibold text-dark-blue mb-3">{title}</h4>
        <div className="flex gap-4">
            <PositionSelector
                label="Left"
                value={config.left}
                onChange={(val) => onChange({ ...config, left: val })}
            />
            <PositionSelector
                label="Right"
                value={config.right}
                onChange={(val) => onChange({ ...config, right: val })}
            />
        </div>
    </div>
);

const PDFExportOptionsModal = ({ isOpen, onClose, onConfirm, defaultOptions }) => {
    const [headerConfig, setHeaderConfig] = useState({ left: 'title', right: 'pageNumber' });
    const [footerConfig, setFooterConfig] = useState({ left: 'none', right: 'none' });

    // Initialize with defaults when modal opens
    useEffect(() => {
        if (isOpen && defaultOptions) {
            if (defaultOptions.header) setHeaderConfig(defaultOptions.header);
            if (defaultOptions.footer) setFooterConfig(defaultOptions.footer);
        }
    }, [isOpen, defaultOptions]);

    const handleConfirm = () => {
        onConfirm({
            header: headerConfig,
            footer: footerConfig
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className="relative z-10 w-full max-w-[500px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-lightGrey bg-white">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple/10 flex items-center justify-center text-purple">
                            <LayoutTemplate size={20} />
                        </div>
                        <h2 className="text-xl font-bold text-dark-blue">PDF Export Options</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 flex flex-col gap-6">
                    <p className="text-sm text-gray-600">
                        Customize the header and footer layout for your PDF export.
                    </p>

                    <SectionConfig
                        title="Header"
                        config={headerConfig}
                        onChange={setHeaderConfig}
                    />

                    <SectionConfig
                        title="Footer"
                        config={footerConfig}
                        onChange={setFooterConfig}
                    />
                </div>

                {/* Footer Buttons */}
                <div className="px-6 py-4 bg-gray-50 border-t border-lightGrey flex justify-end gap-3">
                    <Button
                        variant="outline-gradient-text"
                        onClick={onClose}
                        className="px-6"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        className="px-8"
                    >
                        Export PDF
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PDFExportOptionsModal;
